import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// additional imports
import "./i18n";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FullScreenLoader from "components/common/loaders/FullScreenLoader";

import UiItemsPage from "pages/UiItemsPage";

import { auth } from "./firebase";
import { updateToken } from "store/slices/authReducer";
import moment from "moment";
import { onAuthStateChanged } from "firebase/auth";

// lazy loading of components
const AuthPage = lazy(() => import("pages/AuthPage"));
const HomePage = lazy(() => import("pages/HomePage"));
const AppointmentsPage = lazy(() => import("pages/AppointmentsPage"));
const AppointmentDetailsPage = lazy(() =>
  import("pages/AppointmentDetailsPage")
);
const TreatmentPlanPage = lazy(() => import("pages/TreatmentPlanPage"));
const PatientsPage = lazy(() => import("pages/PatientsPage"));
const ProviderPage = lazy(() => import("pages/ProviderPage"));
const ProviderFormPage = lazy(() => import("pages/ProviderFormPage"));
const EmployeesPage = lazy(() => import("pages/StaffPage"));
const NotFoundPage = lazy(() => import("pages/NotFoundPage"));
const PracticePage = lazy(() => import("pages/PracticePage"));
const ManageLocationPage = lazy(() => import("pages/ManageLocationPage"));

// billings pages
const BillingsDashboardPage = lazy(() => import("pages/BillingsDashboardPage"));

function App() {
  const { isLoggedIn, preAuthUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const myCurrentToken = useSelector((state) => state.auth.user?.accessToken);
  // console.log("Token: ", myCurrentToken);

  useEffect(() => {
    const interval = setInterval(async () => {
      const user = auth.currentUser;
      if (!myCurrentToken || !user) return;
      if (user) {
        // ✅ Do NOT force refresh unless needed
        const refreshedToken = await user.getIdToken();

        // ✅ Only dispatch if the token is different
        if (refreshedToken !== myCurrentToken) {
          dispatch(updateToken(refreshedToken));
          console.log("Token updated in Redux. ", moment().format("hh:mm A"));
        } else {
          console.log("Same token, no dispatch. ", moment().format("hh:mm A"));
        }
      }
    }, 55 * 60 * 1000); // Every 55 minutes

    return () => clearInterval(interval);
  }, [dispatch, myCurrentToken]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in, update Redux state
        dispatch(updateToken(user.accessToken));
      } else {
        // User is signed out, clear Redux state
        // dispatch(clearAuthToken());
        console.log("User is signed out");
      }
    });

    return () => unsubscribe(); // Cleanup listener on unmount
  }, [dispatch]);

  return (
    <div className="App h-screen">
      <Suspense fallback={<FullScreenLoader open={true} />}>
        <Routes>
          <Route
            path="/"
            exact
            element={
              isLoggedIn ? <HomePage /> : <Navigate to="/auth/login" replace />
            }
          />
          <Route
            path="/auth/signup"
            exact
            element={isLoggedIn ? <Navigate to="/" replace /> : <AuthPage />}
          />
          <Route
            path="/auth/login"
            exact
            element={isLoggedIn ? <Navigate to="/" replace /> : <AuthPage />}
          />
          <Route
            path="/auth/forgot-password"
            exact
            element={isLoggedIn ? <Navigate to="/" replace /> : <AuthPage />}
          />
          <Route
            path="/auth/verify-account"
            exact
            element={
              isLoggedIn ? (
                <Navigate to="/" replace />
              ) : preAuthUser !== null ? (
                <AuthPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/practices"
            exact
            element={
              isLoggedIn ? (
                <PracticePage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/locations/location/:id?"
            exact
            element={
              isLoggedIn ? (
                <ManageLocationPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/locations"
            exact
            element={
              isLoggedIn ? (
                <PracticePage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/appointments"
            exact
            element={
              isLoggedIn ? (
                <AppointmentsPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/appointments/appointment/:id?"
            exact
            element={
              isLoggedIn ? (
                <AppointmentDetailsPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/appointments/appointment/:id/treatmentplan/create"
            exact
            element={
              isLoggedIn ? (
                <TreatmentPlanPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/employees"
            exact
            element={
              isLoggedIn ? (
                <EmployeesPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/providers"
            exact
            element={
              isLoggedIn ? (
                <ProviderPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/providers/new"
            exact
            element={
              isLoggedIn ? (
                <ProviderFormPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/providers/edit/:id"
            exact
            element={
              isLoggedIn ? (
                <ProviderFormPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/providers/:id"
            exact
            element={
              isLoggedIn ? (
                <ProviderFormPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/patients"
            exact
            element={
              isLoggedIn ? (
                <PatientsPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/billings"
            exact
            element={
              isLoggedIn ? (
                <BillingsDashboardPage />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
          <Route
            path="/ui"
            exact
            element={
              // <Suspense fallback={<FallBackLoader />}>
              <UiItemsPage />
              // </Suspense>
            }
          />
          <Route path="/NotFoundPage" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/NotFoundPage " replace />} />
        </Routes>
      </Suspense>
      <ToastContainer
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        theme="colored"
      />
    </div>
  );
}

export default App;
